import axios, { type AxiosInstance } from "axios";
import { createContext, type ReactNode, useContext, useState } from "react";

export interface Links {
  current: string;
  next: string | null;
  previous: string | null;
}

export interface ApiErrorDetails {
  error: string;
  error_detail: string;
}

export interface FullApiErrorResponse {
  errors: ApiErrorDetails;
  body: null;
  status_code: number;
}

export interface ApiSuccessBody<TResult> {
  links: Links;
  count: number;
  results: TResult[];
}

export interface FullApiResponseWrapper<TResult> {
  errors: null;
  body: ApiSuccessBody<TResult>;
  status_code: number;
}

interface ApiClientContextValue {
  instance: AxiosInstance | null;
}

const ApiClientContext = createContext<ApiClientContextValue>({
  instance: null,
});

type ApiClientProviderProps = {
  baseURL: string;
  children: ReactNode;
};

export function ApiClientProvider({
  baseURL,
  children,
}: ApiClientProviderProps) {
  const [instance] = useState<AxiosInstance>(() =>
    axios.create({
      baseURL,
      timeout: 30000,
    }),
  );

  return (
    <ApiClientContext.Provider value={{ instance }}>
      {children}
    </ApiClientContext.Provider>
  );
}

export function useApiClient(): AxiosInstance {
  const { instance } = useContext(ApiClientContext);
  if (!instance) {
    throw new Error("useApiClient must be used within ApiClientProvider");
  }
  return instance;
}
